import React, {FC, memo} from "react";
import {Alert, Table, Tag} from "antd";
import {ApolloError} from "@apollo/client";
import moment from "moment";
import {green, orange, yellow} from "@ant-design/colors";
import {LessonBookingPlannerPreviewQuery, LessonBookingPlannerRateStatus} from "../../../generated/graphql";
import SummarySection from "../../../views/SummarySection/SummarySection";
import LoadingContent from "../../../views/LoadingContent/LoadingContent";
import {renderAmount} from "../../../helpers/renderAmount";
import css from "./LessonBookings.less";

// Vorschau „Neu“ (Superuser): Preis = Einheitenpreis × gebuchte Einheiten, Abweichungen mit Grund,
// gebundene Raten unverändert, Ausgleich auf der Rate des betroffenen Monats. Nur lesend.

type PlannerData = LessonBookingPlannerPreviewQuery["lessonBookingPlannerPreview"];
type PlannerRate = PlannerData["rates"][number];

type PlannerPreviewProps = {
    data?: PlannerData;
    loading: boolean;
    error?: ApolloError;
};

const eur = (amount: number) => renderAmount({amount});
const signed = (amount: number) => `${amount < 0 ? "−" : "+"} ${eur(Math.abs(amount))}`;
const date = (value: string | Date) => moment(value).format("DD.MM.YYYY");

const statusTag = (rate: PlannerRate) => {
    if (rate.status === LessonBookingPlannerRateStatus.Bound) {
        return <Tag>{`gebunden: ${rate.boundReason ?? ""}`}</Tag>;
    }
    if (rate.status === LessonBookingPlannerRateStatus.New) {
        return <Tag color="green">{"neu"}</Tag>;
    }

    return <Tag color="blue">{"offen"}</Tag>;
};

const PlannerPreview: FC<PlannerPreviewProps> = ({data, loading, error}) => {
    if (error) {
        return (
            <div style={{padding: "1rem"}}>
                <Alert type="error" showIcon message="Vorschau (neues Modell) konnte nicht berechnet werden" description={error.message} />
            </div>
        );
    }
    if (loading || !data) {
        return (
            <div style={{height: "70%"}}>
                <LoadingContent content="Vorschau (neues Modell) wird berechnet..." />
            </div>
        );
    }

    const priceChanged = Math.abs(data.priceAfter - data.priceBefore) > 0.005;
    const unitsLine =
        data.unitPrice === null || data.unitPrice === undefined
            ? `${data.bookedUnitsBefore} Einheiten, Pauschalpreis pro Kurs`
            : `${data.bookedUnitsBefore} → ${data.bookedUnitsAfter} Einheiten à ${eur(data.unitPrice)}`;

    const columns = [
        {title: "Fällig", dataIndex: "paymentDue", key: "due", render: (value: string) => date(value)},
        {title: "Status", key: "status", render: (_: unknown, rate: PlannerRate) => statusTag(rate)},
        {
            title: "Heute",
            dataIndex: "amountBefore",
            key: "before",
            align: "right" as const,
            render: (value: number, rate: PlannerRate) => (rate.status === LessonBookingPlannerRateStatus.New ? "–" : eur(value)),
        },
        {
            title: "Neu",
            dataIndex: "amountAfter",
            key: "after",
            align: "right" as const,
            render: (value: number, rate: PlannerRate) => <span style={{fontWeight: Math.abs(value - rate.amountBefore) > 0.005 ? 600 : 400}}>{eur(value)}</span>,
        },
        {
            title: "Ausgleich",
            key: "adjustments",
            render: (_: unknown, rate: PlannerRate) =>
                rate.adjustments.length === 0 ? null : (
                    <div>
                        {rate.adjustments.map((adjustment, index) => (
                            <div key={index} style={{color: orange[7]}}>{`${adjustment.reason} ${signed(adjustment.amount)}`}</div>
                        ))}
                    </div>
                ),
        },
    ];

    return (
        <div className={css.bookingRoot}>
            <SummarySection
                title="Preis (neues Modell)"
                content={
                    <div style={{padding: "4px 8px"}}>
                        <div>{unitsLine}</div>
                        <div style={{fontWeight: 600}}>{priceChanged ? `Preis ${eur(data.priceBefore)} → ${eur(data.priceAfter)}` : `Preis ${eur(data.priceBefore)} (unverändert)`}</div>
                        {data.components.map((component, index) => (
                            <div key={index} style={{color: orange[7]}}>{`${component.reason} ${signed(component.amount)}`}</div>
                        ))}
                        {Math.abs(data.legacyGap) > 0.005 && (
                            <div style={{color: orange[7]}}>{`${data.legacyGap < 0 ? "Nachlass" : "Aufschlag"} „Altbestand“ ${signed(data.legacyGap)} (Grund vom Büro nachzutragen)`}</div>
                        )}
                    </div>
                }
            />
            <SummarySection
                title="Raten (neues Modell)"
                content={
                    <div style={{padding: "4px 8px"}}>
                        <Table<PlannerRate>
                            size="small"
                            pagination={false}
                            rowKey={(rate) => rate.id ?? `new-${String(rate.paymentDue)}`}
                            dataSource={data.rates}
                            columns={columns}
                            onRow={(rate) => ({style: rate.adjustments.length > 0 ? {backgroundColor: yellow[0]} : rate.status === LessonBookingPlannerRateStatus.Bound ? {color: "#999"} : {}})}
                        />
                        {data.credit > 0.005 && <Alert style={{marginTop: 8}} type="info" showIcon message={`Guthaben ${eur(data.credit)}: kein offener Posten kann den Ausgleich tragen.`} />}
                    </div>
                }
            />
            <div style={{padding: "4px 8px"}}>
                {data.canceled ? (
                    <Alert
                        type="info"
                        showIcon
                        message="Buchung ist storniert"
                        description={
                            <div>
                                {"Einheiten werden abgeglichen, Preis und Raten bleiben unverändert."}
                                {data.warnings.length > 0 && (
                                    <ul style={{margin: "4px 0 0", paddingLeft: "1.25rem"}}>
                                        {data.warnings.map((warning, index) => (
                                            <li key={index}>{warning}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        }
                    />
                ) : data.warnings.length === 0 ? (
                    <Alert type="success" showIcon message="Buchung konsistent: Preis = Einheiten × Einheitenpreis, Raten decken den Preis." style={{borderColor: green[3]}} />
                ) : (
                    <Alert
                        type="warning"
                        showIcon
                        message="Büro muss entscheiden"
                        description={
                            <ul style={{margin: 0, paddingLeft: "1.25rem"}}>
                                {data.warnings.map((warning, index) => (
                                    <li key={index}>{warning}</li>
                                ))}
                            </ul>
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default memo(PlannerPreview);
